import type { CSSProperties, ReactNode } from 'react';
import { HYPE_LIST, MOODS } from '@racksavant/core';
import { DEVELOPING_LINES } from '@racksavant/ui';
import { cx } from '../cx';
import { Cutout, Face, Plate } from './Photo';
import s from './FeatureArt.module.css';

/* =========================================================================
   Feature art.

   Everything here used to be vector clip-art: little gradient rectangles
   standing in for garments, emoji in pills standing in for reactions. All of
   it is gone. What replaces it is either a photograph or type — usually both,
   with the type sitting on the photograph rather than beside it.
   ========================================================================= */

export type FeatureArtKind =
  | 'rack'
  | 'develop'
  | 'hype'
  | 'lookbooks'
  | 'captions'
  | 'stylist'
  | 'exports'
  | 'drops';

function color(hex: string): CSSProperties {
  return { '--c': hex } as CSSProperties;
}

function Panel({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={s.panel}>
      <span className={s.panelLabel}>{label}</span>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ rack */

const RACK_ITEMS = [
  { name: 'Cropped moto jacket', mood: MOODS.feral },
  { name: 'Ivory slip', mood: MOODS.soft },
  { name: 'Wool overcoat', mood: MOODS.regal },
  { name: 'Mesh long sleeve', mood: MOODS.futuristic },
  { name: 'Carpenter jean', mood: MOODS.grunge },
  { name: 'Silk column dress', mood: MOODS.minimal },
];

function Rack() {
  return (
    <Panel label="The rack, pulled by mood">
      <Cutout id="coat" width={230} tilt={-4} className={s.rackCoat} />
      <ol className={s.rackList}>
        {RACK_ITEMS.map((item, i) => (
          <li key={item.name} className={s.rackRow} style={color(item.mood.color)}>
            <span className={s.rackIndex}>{String(i + 1).padStart(2, '0')}</span>
            <span className={s.rackName}>{item.name}</span>
            <span className={s.rackMood}>{item.mood.label.toLowerCase()}</span>
            <span className={s.rackSwatch} aria-hidden="true" />
          </li>
        ))}
      </ol>
    </Panel>
  );
}

/* --------------------------------------------------------------- develop */

function Develop() {
  const shown = [
    DEVELOPING_LINES[0],
    DEVELOPING_LINES[2],
    DEVELOPING_LINES[4],
    DEVELOPING_LINES[6],
    DEVELOPING_LINES[7],
  ];
  const activeIndex = 3;

  return (
    <div className={s.developWrap}>
      <Plate id="mirror" className={s.developPlate} caption={false} ratio={3 / 4} />
      <Panel label="Developing">
        <ol className={s.stages}>
          {shown.map((line, i) => (
            <li
              key={line}
              className={cx(
                s.stage,
                i < activeIndex && s.stageDone,
                i === activeIndex && s.stageNow,
              )}
            >
              <span className={s.stageIndex}>{String(i + 1).padStart(2, '0')}</span>
              {line}
            </li>
          ))}
        </ol>
        <div className={s.progressTrack}>
          <div className={s.progressFill} />
        </div>
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ hype */

function HypeArt() {
  return (
    <Panel label="Seven ways to say something">
      <ul className={s.hypeCloud}>
        {HYPE_LIST.map((h, i) => (
          <li key={h.kind} className={s.hypeItem}>
            <Face kind={h.kind} size={i % 3 === 0 ? 104 : 84} tilt={[-6, 4, -2, 7, -4, 3, -5][i]} labelled />
            <span className={s.hypeName}>{h.label}</span>
          </li>
        ))}
      </ul>
      <p className={s.hypeNote}>
        Someone called you a menace and someone said soft launch it. That is a read. A heart is not.
      </p>
    </Panel>
  );
}

/* ------------------------------------------------------------- lookbooks */

const ERAS = [
  { name: 'Feral summer', mood: MOODS.feral, meta: 'Jun–Aug' },
  { name: 'Quiet money', mood: MOODS.minimal, meta: 'ongoing' },
  { name: 'The Berlin week', mood: MOODS.moody, meta: 'closed' },
  { name: 'Soft launch', mood: MOODS.soft, meta: 'private' },
];

function Lookbooks() {
  return (
    <div className={s.eraWrap}>
      <Plate id="street" className={s.eraPlate} caption={false} ratio={4 / 5} />
      <Panel label="Lookbooks · your eras">
        <ul className={s.eras}>
          {ERAS.map((era) => (
            <li key={era.name} className={s.era} style={color(era.mood.color)}>
              <span className={s.eraSpine} aria-hidden="true" />
              <span className={s.eraName}>{era.name}</span>
              <span className={s.eraMeta}>{era.meta}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------- captions */

const TONES = ['Deadpan', 'Soft launch', 'Unhinged', 'Poetic', 'Blunt', 'Devastating'];

function Captions() {
  return (
    <Panel label="Caption studio">
      <ul className={s.tones}>
        {TONES.map((tone, i) => (
          <li key={tone} className={cx(s.tone, i === 0 && s.toneOn)}>
            {tone}
          </li>
        ))}
      </ul>
      <div className={s.captionOut}>
        <p className={s.captionLine}>no thoughts. one coat.</p>
        <p className={s.captionLine}>this is what I wore to ruin someone’s week.</p>
      </div>
      <span className={s.meta}>Reroll until it sounds like you · copies with the look</span>
    </Panel>
  );
}

/* --------------------------------------------------------------- stylist */

function Stylist() {
  return (
    <Panel label="Savant">
      <div className={s.transcript}>
        <p className={s.said}>
          <span className={s.who}>You</span>
          wedding in october, not the bride, do not want to look like I tried
        </p>
        <p className={cx(s.said, s.saidSavant)}>
          <span className={s.who}>Savant</span>
          Then we are going regal, not romantic. Column silhouette, one deep colour, nothing shiny.
          I have pulled three from the rack — the olive one is the answer, but try the oxblood so
          you know why.
        </p>
        <p className={s.said}>
          <span className={s.who}>You</span>
          put the olive in the room
        </p>
      </div>
      <Cutout id="dress" width={190} tilt={3} className={s.stylistCutout} />
    </Panel>
  );
}

/* --------------------------------------------------------------- exports */

function Exports() {
  return (
    <Panel label="Exports, pre-cut">
      <div className={s.frames}>
        <figure className={s.frameCell}>
          <Plate id="guy" caption={false} ratio={9 / 16} />
          <figcaption className={s.frameLabel}>9:16 story</figcaption>
        </figure>
        <figure className={s.frameCell}>
          <Plate id="hero" caption={false} ratio={4 / 5} />
          <figcaption className={s.frameLabel}>4:5 feed</figcaption>
        </figure>
        <figure className={s.frameCell}>
          <Plate id="detail" caption={false} ratio={1} />
          <figcaption className={s.frameLabel}>Before / after</figcaption>
        </figure>
      </div>
      <span className={s.meta}>No watermark on paid plans · file names you can read</span>
    </Panel>
  );
}

/* ----------------------------------------------------------------- drops */

function Drops() {
  return (
    <Panel label="A drop">
      <div className={s.dropHead}>
        <span className={s.dropWho}>@novaeats</span>
      </div>
      <h4 className={s.dropTitle}>The October Eight</h4>
      <div className={s.dropRow}>
        <Cutout id="jacket" width={150} tilt={-3} />
        <Cutout id="bag" width={78} tilt={4} />
        <Cutout id="sunglasses" width={128} tilt={-6} />
        <Cutout id="boots" width={92} tilt={2} />
      </div>
      <p className={s.dropMeta}>Eight pieces your followers can put themselves in</p>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */

export function FeatureArt({ kind }: { kind: FeatureArtKind }) {
  switch (kind) {
    case 'rack':
      return <Rack />;
    case 'develop':
      return <Develop />;
    case 'hype':
      return <HypeArt />;
    case 'lookbooks':
      return <Lookbooks />;
    case 'captions':
      return <Captions />;
    case 'stylist':
      return <Stylist />;
    case 'exports':
      return <Exports />;
    case 'drops':
      return <Drops />;
    default:
      return null;
  }
}
